import { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, Button, DatePicker, Icon, Switch, Tooltip, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type ConversationScheduleStore } from 'widgets/ConversationSchedule/store/conversationScheduleStore'
import styles from './styles.module.scss'

type IScheduleContentProps = {
  store: ConversationScheduleStore
}

export const ScheduleContent: FC<IScheduleContentProps> = observer(({ store }) => {
  const {
    is_contact_timezone,
    stop_on_response,
    handleSchedule,
    toggleIsUseContactTimezone,
    toggleIsCancelOnResponse,
    scheduleText,
    scheduleDate,
    setScheduleDate,
    isAlreadyPassed,
    contactDateString,
    offsetMinutes,
    isQuietHoursBlockOnWeekend,
    quietHoursAllowedTimeFrom,
    quietHoursAllowedTimeTo,
    isScheduleMessageInsideQuietHours,
  } = store
  const { featureFlagsStore } = useEntitiesContext()

  const effectiveOffsetMinutes = is_contact_timezone ? offsetMinutes : 0

  return (
    <div className={styles.wrap}>
      {isAlreadyPassed && (
        <Alert
          item={{
            type: 'error',
            title: 'The scheduled time for this contact has already passed',
          }}
          marginBottom={16}
        />
      )}
      {featureFlagsStore.quiet_hours && isScheduleMessageInsideQuietHours && (
        <Alert
          item={{
            type: 'warning',
            title: 'Recommended sending times:',
            desc: (
              <>
                Monday - {isQuietHoursBlockOnWeekend ? 'Friday' : 'Sunday'}:{' '}
                {quietHoursAllowedTimeFrom} - {quietHoursAllowedTimeTo}
                <br />
                {isQuietHoursBlockOnWeekend && <>Saturday - Sunday: Don’t send</>}
              </>
            ),
          }}
          marginBottom={16}
        />
      )}
      <DatePicker
        type={'singleExpanded'}
        date={scheduleDate.toDate()}
        handleDate={setScheduleDate}
        excludeDateIntervals={[
          {
            start: new Date(0),
            end: uiStore.dayjs().add(effectiveOffsetMinutes, 'minutes').add(-1, 'day').toDate(),
          },
        ]}
        filteredPassedTime={uiStore.dayjs().add(effectiveOffsetMinutes, 'minutes').toDate()}
      />
      <div className={styles.wrapSwitches}>
        <Button
          titleContent={
            <>
              <Typography
                variant={'text-input-field'}
                color={'tertiary'}
                className={'row8'}
                ariaLabel={'Use contact timezone'}
              >
                <Icon icon='userFilled' />
                Use contact timezone
              </Typography>
              <Switch value={is_contact_timezone} />
            </>
          }
          size={'medium'}
          typeBtn={'switchBtn'}
          fullWidth
          onClick={toggleIsUseContactTimezone}
        />
        <Button
          titleContent={
            <>
              <div className={'row4'}>
                <Typography
                  variant={'text-input-field'}
                  color={'tertiary'}
                  className={'row8'}
                  ariaLabel={'Cancel on response'}
                >
                  <Icon icon='messageX' />
                  Cancel on response
                </Typography>
                <Tooltip
                  label={
                    'Your scheduled message will be canceled if you receive an inbound message before the scheduled date'
                  }
                  placement={'top'}
                  width={238}
                  breakWord
                >
                  <Icon icon={'question'} fontSize={16} color={'var(--gray-40)'} />
                </Tooltip>
              </div>

              <Switch value={stop_on_response} />
            </>
          }
          size={'medium'}
          typeBtn={'switchBtn'}
          fullWidth
          onClick={toggleIsCancelOnResponse}
        />
      </div>
      <Button
        text={scheduleText}
        size={'medium'}
        typeBtn={'contained'}
        contained={'primary'}
        disabled={isAlreadyPassed}
        fullWidth
        onClick={handleSchedule}
      />
      {!is_contact_timezone && (
        <Typography
          variant={'body-sm-regular'}
          color={'tertiary'}
          className={styles.contactTime}
          tag={'div'}
          ariaLabel={'Contact'}
        >
          Contact’s time - {contactDateString}
        </Typography>
      )}
    </div>
  )
})
